// apps/admin-ui/src/components/cms/SpaceSwitcher.tsx
import { useState, useRef, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ChevronDown, Check, LayoutGrid } from 'lucide-react';
import { api } from '../../lib/api';

interface Space {
  id: string;
  name: string;
  slug: string;
  type?: string;
}

export function SpaceSwitcher() {
  const { spaceId: activeId } = useParams<{ spaceId: string }>();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const { data: spaces = [] } = useQuery<Space[]>({
    queryKey: ['cms-spaces-switcher'],
    queryFn: () => api.get('/api/v1/cms/spaces').then((r) => r.data),
    staleTime: 60_000,
  });

  const activeSpace = spaces.find((s) => s.id === activeId);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  function selectSpace(id: string) {
    setOpen(false);
    navigate(`/spaces/${id}/pages`);
  }

  return (
    <div className="px-2 mb-2 relative" ref={ref}>
      <button
        onClick={() => setOpen((v) => !v)}
        aria-label="Switch space"
        aria-expanded={open}
        className="w-full flex items-center justify-between gap-2 px-2.5 py-2 rounded-lg bg-zinc-100 hover:bg-zinc-200 transition-colors text-left"
      >
        <div className="min-w-0">
          <div className="text-xs font-semibold text-zinc-800 truncate">
            {activeSpace?.name ?? 'Select space'}
          </div>
          {activeSpace && (
            <div className="text-[10px] text-zinc-400 truncate">
              {activeSpace.type ?? ''}
            </div>
          )}
        </div>
        <ChevronDown size={13} className="text-zinc-400 shrink-0" />
      </button>

      {open && (
        <div className="absolute left-2 right-2 top-full mt-1 bg-white border border-zinc-200 rounded-lg shadow-lg z-50 overflow-hidden">
          {spaces.map((s) => (
            <button
              key={s.id}
              onClick={() => selectSpace(s.id)}
              className="w-full flex items-center gap-2 px-3 py-2 hover:bg-zinc-50 transition-colors text-left border-b border-zinc-100 last:border-b-0"
            >
              <Check
                size={12}
                className={s.id === activeId ? 'text-violet-600' : 'text-transparent'}
              />
              <div className="min-w-0">
                <div className="text-xs font-semibold text-zinc-800 truncate">{s.name}</div>
                {s.type && (
                  <div className="text-[10px] text-zinc-400">{s.type}</div>
                )}
              </div>
            </button>
          ))}
          <button
            onClick={() => {
              setOpen(false);
              navigate('/spaces', { state: { manage: true } });
            }}
            className="w-full flex items-center gap-2 px-3 py-2 text-xs text-zinc-400 hover:text-violet-600 hover:bg-zinc-50 transition-colors border-t border-zinc-100"
          >
            <LayoutGrid size={12} /> Manage spaces…
          </button>
        </div>
      )}
    </div>
  );
}
